import { useCallback } from "react"
import {
  Block,
  Button,
  CoinsHand,
  FlexGrid,
  FlexGridItem,
  LabelSmall,
  MatsCircle,
  Settings03,
  useStyletron,
} from "@mezo-org/mezo-clay"
import { Link } from "@tanstack/react-router"

import {
  selectHasLoadedWalletBalances,
  selectIsBorrowLoadingData,
  selectUserHasLoan,
  selectWalletBalances,
  useDappSelector,
} from "#/redux-state"
import { IS_MEZO_MAINNET, routes } from "#/shared/constants"
import { usePosthogButtonClickedEvent } from "#/shared/hooks/posthog-events"
import { PosthogAnalyticsId } from "#/shared/types"
import {
  bigIntToCommaSeparatedFloat,
  getCurrencySymbol,
  networkUtils,
} from "#/shared/utils"
import { TokenIcon } from "#/ui/Token"

import HomepageMainSectionAssetsBorrowAmount from "./HomepageMainSectionAssetsBorrowAmount"
import HomepageMainSectionAssetsItem from "./HomepageMainSectionAssetsItem"

export default function HomepageMainSectionAssetsMusd() {
  const { MUSD } = useDappSelector(selectWalletBalances)
  const hasLoan = useDappSelector(selectUserHasLoan)

  const hasLoadedWalletBalances = useDappSelector(selectHasLoadedWalletBalances)
  const isLoadingBorrowData = useDappSelector(selectIsBorrowLoadingData)

  const { emitButtonClickedEvent } = usePosthogButtonClickedEvent()
  const [css] = useStyletron()

  const borrowButtonHandler = useCallback(() => {
    if (hasLoan) return

    emitButtonClickedEvent(PosthogAnalyticsId.MUSD_BORROW)
  }, [hasLoan, emitButtonClickedEvent])

  return (
    <HomepageMainSectionAssetsItem
      isLoading={!hasLoadedWalletBalances}
      title={getCurrencySymbol("MUSD")}
      header={
        <FlexGrid
          flexGridColumnCount={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <FlexGridItem>
            <TokenIcon token="MUSD" />
          </FlexGridItem>
          {!IS_MEZO_MAINNET && (
            <FlexGridItem
              overrides={{
                Block: { style: { flexGrow: 0, width: "auto" } },
              }}
            >
              <FlexGrid
                flexGridColumnCount={2}
                backgroundColor="gray50"
                alignItems="center"
                flexGridColumnGap="scale100"
                padding="4px 8px 4px 4px"
                overrides={{
                  Block: { style: { borderRadius: "32px" } },
                }}
              >
                <FlexGridItem
                  overrides={{
                    Block: {
                      style: () => ({
                        flexGrow: 0,
                        width: "auto",
                        height: "18px",
                      }),
                    },
                  }}
                >
                  <MatsCircle size={16} />
                </FlexGridItem>
                <FlexGridItem
                  overrides={{
                    Block: { style: { flexGrow: 0, width: "auto" } },
                  }}
                >
                  <LabelSmall
                    color="contentTertiary"
                    style={{ textTransform: "capitalize" }}
                  >
                    {networkUtils.getMezoNetworkLabel()}
                  </LabelSmall>
                </FlexGridItem>
              </FlexGrid>
            </FlexGridItem>
          )}
        </FlexGrid>
      }
      value={
        <>
          <Block
            as="span"
            className={css({
              color: MUSD.balance > 0n ? "black" : "#A6A6A6",
            })}
          >
            {bigIntToCommaSeparatedFloat(
              MUSD.balance,
              Number(MUSD.decimals),
              Number(MUSD.decimals),
            )}
          </Block>{" "}
          MUSD
        </>
      }
      label={<HomepageMainSectionAssetsBorrowAmount />}
      buttons={[
        ...(hasLoadedWalletBalances && !isLoadingBorrowData
          ? [
              <Button
                data-attr={
                  hasLoan ? "update_loan_cta" : "main-start_borrow_cta"
                }
                $as={Link}
                onClick={borrowButtonHandler}
                href={routes.BORROW}
                size="small"
                overrides={{
                  BaseButton: { style: () => ({ width: "100px" }) },
                }}
                startEnhancer={
                  hasLoan ? (
                    <Settings03 size={16} color="white" />
                  ) : (
                    <CoinsHand size={16} color="white" />
                  )
                }
              >
                {hasLoan ? "Manage" : "Borrow"}
              </Button>,
            ]
          : []),
        ...(hasLoadedWalletBalances && MUSD.balance > 0n
          ? [
              <Button
                $as={Link}
                href={routes.MARKET}
                size="small"
                overrides={{
                  BaseButton: { style: () => ({ width: "100px" }) },
                }}
                startEnhancer={<CoinsHand size={16} color="white" />}
              >
                Spend
              </Button>,
            ]
          : []),
      ]}
    />
  )
}
